// Inventory changelog tab body -- a per-event timeline of material
// and recipe cost entries (receipts, recipe counts, adjustments,
// opening balances) for oversight. Renders inline (no SectionCard wrapper)
// so the ManufacturingTabbedSection chrome owns the card frame.
//
// MVP scope: org-wide timeline, filter by entry_type (no material-
// pick filter or date range yet -- additive in a follow-up). Pagination
// is server-side (limit + offset) keyed off `useState` here; the page
// state intentionally lives in this component so switching tabs
// resets to page 0 (cheap; matches "fresh oversight glance" intent).
//
// File-size hygiene: keep this body close to the materials list
// shape. Per-entry row helper at the bottom; pagination controls
// inline (the materials list has its own MaterialsPaginationBar
// extracted internally; reuse-grade extraction can come later when
// a third paginated list lands).
import { useEffect, useMemo, useState } from "react";
import { mfgXsInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { INVENTORY_CHANGELOG_REFRESH_EVENT } from "@/lib/manufacturing/inventoryChangelogRefreshEvent";
import type {
  MfgInventoryChangelogLedgerKind,
  MfgInventoryChangelogRow,
  MfgInventoryCostEntryType,
} from "@/lib/manufacturing/types";
import { useManufacturingInventoryChangelog } from "./useManufacturingInventoryChangelog";
import { ManufacturingChangelogReceiptLinker } from "./ManufacturingChangelogReceiptLinker";
import { ManufacturingInventoryChangelogRow } from "./ManufacturingInventoryChangelogRow";

const PAGE_SIZES = [10, 20, 50, 100] as const;
type PageSize = (typeof PAGE_SIZES)[number];
const DEFAULT_PAGE_SIZE: PageSize = 20;

type Props = {
  enabled: boolean;
};

export function ManufacturingInventoryChangelogBody({ enabled }: Props) {
  const [entryTypeFilter, setEntryTypeFilter] = useState<"all" | MfgInventoryCostEntryType>("all");
  const [ledgerKindFilter, setLedgerKindFilter] = useState<"all" | MfgInventoryChangelogLedgerKind>(
    "all",
  );
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  // Reset to page 0 when filter or page size changes so the view is
  // never stranded on an empty trailing page.
  useEffect(() => {
    setPage(0);
  }, [entryTypeFilter, ledgerKindFilter, pageSize]);

  const filters = useMemo(
    () => ({
      entry_type: entryTypeFilter === "all" ? undefined : entryTypeFilter,
      ledger_kind: ledgerKindFilter === "all" ? undefined : ledgerKindFilter,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [entryTypeFilter, ledgerKindFilter, pageSize, page],
  );

  const { entries, total, loading, working, error, refresh } = useManufacturingInventoryChangelog({
    enabled,
    filters,
  });

  useEffect(() => {
    if (!enabled) return;
    const onRefresh = () => {
      void refresh();
    };
    window.addEventListener(INVENTORY_CHANGELOG_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(INVENTORY_CHANGELOG_REFRESH_EVENT, onRefresh);
  }, [enabled, refresh]);

  const [linkingRow, setLinkingRow] = useState<MfgInventoryChangelogRow | null>(null);

  const pageCount = total > 0 ? Math.ceil(total / pageSize) : 1;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const rangeStart = total === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, total);
  const canPrev = safePage > 0;
  const canNext = total > 0 && safePage < pageCount - 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-zinc-400">
          Type
          <select
            className={mfgXsInputClass}
            value={entryTypeFilter}
            onChange={(e) =>
              setEntryTypeFilter(e.target.value as "all" | MfgInventoryCostEntryType)
            }
          >
            <option value="all">All</option>
            <option value="receive">Receive</option>
            <option value="manual">Manual</option>
            <option value="adjustment">Adjustment</option>
            <option value="opening_balance">Opening balance</option>
            <option value="build">Build</option>
          </select>
        </label>
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-zinc-400">
          Ledger
          <select
            className={mfgXsInputClass}
            value={ledgerKindFilter}
            onChange={(e) =>
              setLedgerKindFilter(e.target.value as "all" | MfgInventoryChangelogLedgerKind)
            }
          >
            <option value="all">Materials + recipes</option>
            <option value="material">Materials</option>
            <option value="recipe">Recipes</option>
          </select>
        </label>
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-zinc-400">
          Per page
          <select
            className={mfgXsInputClass}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value) as PageSize)}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
          onClick={() => void refresh()}
          disabled={working}
        >
          {working ? "Refreshing…" : "Refresh"}
        </button>
        <p className="ml-auto text-[11px] text-gray-600 dark:text-zinc-400">
          {total === 0 ? (
            "No entries"
          ) : (
            <>
              Showing{" "}
              <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{rangeStart}</span>
              –<span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{rangeEnd}</span>{" "}
              of <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{total}</span>
            </>
          )}
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading changelog…</div>
      ) : entries.length === 0 && !error ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400">
          No inventory events yet. Post a receipt, recipe count, opening balance, or complete an
          inventory count (adjustments) to start the timeline.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
          {entries.map((row) => (
            <ManufacturingInventoryChangelogRow
              key={`${row.ledger_kind}-${row.id}`}
              row={row}
              onLinkClick={() => {
                if (row.ledger_kind === "material") setLinkingRow(row);
              }}
            />
          ))}
        </ul>
      )}

      {linkingRow && linkingRow.ledger_kind === "material" ? (
        <ManufacturingChangelogReceiptLinker
          row={linkingRow}
          onClose={() => setLinkingRow(null)}
          onLinked={() => void refresh()}
        />
      ) : null}

      {total > pageSize ? (
        <div className="flex items-center justify-end gap-1 border-t border-gray-200 pt-2 dark:border-zinc-700">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setPage(safePage - 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Prev
          </button>
          <span className="px-1 text-[11px] tabular-nums text-gray-600 dark:text-zinc-400">
            {safePage + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setPage(safePage + 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

